import type { LayerGroup, Map as LeafletMap } from "leaflet";
import {
  GATEWAY_MAP_SIZE,
  gatewayStoredMapPointToVulnona,
  gatewayVulnonaMapPointToStored,
  gatewayWorldRadiusBoundaryVulnona
} from "@isle/domain";
import type { EcosystemZoneSettings } from "./types.js";
import { GATEWAY_BASEMAP_URL } from "./live-map.js";

type Zone = EcosystemZoneSettings["config"]["aiZones"][number] | EcosystemZoneSettings["config"]["plantZones"][number];

let activeMap: LeafletMap | undefined;
let activeZoneLayer: LayerGroup | undefined;
let clickHandler: ((mapX: number, mapY: number) => void) | undefined;
let savedView: { center: import("leaflet").LatLng; zoom: number } | undefined;

export async function initializeEcosystemZoneMap(
  zones: Zone[],
  selectedZoneId: string | undefined,
  onPlace: (mapX: number, mapY: number) => void
): Promise<void> {
  disposeEcosystemZoneMap();
  const container = document.querySelector<HTMLElement>("#ecosystemZoneMap");
  if (!container) return;
  try {
    const leafletModule = await import("leaflet");
    const L = (leafletModule as { default?: typeof import("leaflet") }).default ?? leafletModule;
    const bounds = L.latLngBounds([[0, 0], [GATEWAY_MAP_SIZE, GATEWAY_MAP_SIZE]]);
    const map = L.map(container, {
      crs: L.CRS.Simple,
      minZoom: -14,
      maxZoom: 4,
      zoomSnap: 0.25,
      zoomDelta: 0.5,
      wheelDebounceTime: 25,
      wheelPxPerZoomLevel: 80,
      zoomAnimation: true,
      fadeAnimation: true,
      maxBounds: bounds.pad(0.12),
      maxBoundsViscosity: 0.9,
      attributionControl: false
    });
    activeMap = map;
    clickHandler = onPlace;
    L.imageOverlay(GATEWAY_BASEMAP_URL, bounds, { interactive: false }).addTo(map);
    activeZoneLayer = L.layerGroup().addTo(map);
    renderZones(L, zones, selectedZoneId);
    const overviewZoom = map.getBoundsZoom(bounds, false, L.point(24, 24));
    map.setMinZoom(overviewZoom);
    map.setMaxZoom(overviewZoom + 8);
    if (savedView) {
      map.setView(savedView.center, clamp(savedView.zoom, overviewZoom, overviewZoom + 8), { animate: false });
    } else {
      map.fitBounds(bounds, { animate: false, padding: [24, 24] });
    }
    map.on("click", (event: import("leaflet").LeafletMouseEvent) => {
      const mapX = clamp(event.latlng.lng, 0, GATEWAY_MAP_SIZE);
      const mapY = clamp(GATEWAY_MAP_SIZE - event.latlng.lat, 0, GATEWAY_MAP_SIZE);
      const storedPoint = gatewayVulnonaMapPointToStored(mapX, mapY);
      clickHandler?.(round(storedPoint.mapX), round(storedPoint.mapY));
    });
    map.whenReady(() => map.invalidateSize());
  } catch (error) {
    console.error("Ecosystem zone map initialization failed.", error);
    container.innerHTML = '<p class="map-runtime-error">The Gateway zone editor could not be loaded.</p>';
  }
}

export function refreshEcosystemZoneMap(zones: Zone[], selectedZoneId: string | undefined): void {
  if (!activeMap || !activeZoneLayer) return;
  void import("leaflet").then((leafletModule) => {
    const L = (leafletModule as { default?: typeof import("leaflet") }).default ?? leafletModule;
    activeZoneLayer?.clearLayers();
    renderZones(L, zones, selectedZoneId);
  });
}

export function disposeEcosystemZoneMap(): void {
  clickHandler = undefined;
  activeZoneLayer = undefined;
  if (activeMap) {
    savedView = { center: activeMap.getCenter(), zoom: activeMap.getZoom() };
    activeMap.off();
    activeMap.remove();
  }
  activeMap = undefined;
}

function renderZones(
  L: typeof import("leaflet"),
  zones: Zone[],
  selectedZoneId: string | undefined
): void {
  if (!activeMap || !activeZoneLayer) return;
  for (const zone of zones) {
    const isPlant = "countPerRestart" in zone;
    const markerColor = isPlant ? "#2f9a67" : zone.markerColor;
    const selected = zone.id === selectedZoneId;
    const displayedCenter = gatewayStoredMapPointToVulnona(zone.center.mapX, zone.center.mapY);
    const position: [number, number] = [GATEWAY_MAP_SIZE - displayedCenter.mapY, displayedCenter.mapX];
    const boundary = gatewayWorldRadiusBoundaryVulnona(
      zone.center.mapX,
      zone.center.mapY,
      zone.radiusWorldUnits
    ).map((point) => [GATEWAY_MAP_SIZE - point.mapY, point.mapX] as [number, number]);
    const circle = L.polygon(boundary, {
      color: selected ? "#f5bf45" : isPlant ? "#67d293" : markerColor,
      weight: selected ? 3 : 2,
      fillColor: markerColor,
      fillOpacity: zone.enabled ? 0.22 : 0.08,
      dashArray: zone.enabled ? undefined : "6 5",
      interactive: false
    }).addTo(activeZoneLayer);
    L.circleMarker(position, {
      radius: selected ? 5 : 3,
      color: selected ? "#ffe09b" : "#d7eadf",
      weight: 1,
      fillColor: markerColor,
      fillOpacity: 1,
      interactive: false
    }).addTo(activeZoneLayer);
    circle.bindTooltip(`${zone.name} // ${isPlant ? "plants" : "AI"} // ${zone.radiusWorldUnits.toLocaleString()} uu radius`, {
      permanent: selected,
      direction: "top",
      className: "ecosystem-zone-label"
    });
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}
